import styles from './index.module.css'
import cn from 'clsx'

import React, { useCallback, useMemo, useRef, useState } from 'react'
import type { Point, Rect } from 'types/screen'
import { getPageXOffset, getPageYOffset } from '@disco/utils/dom'
import { keyEqualTo } from '@disco/utils/keyboard'
import useClickOutside from 'hooks/use-click-outside'
import useEventListener from 'hooks/use-event-listener'
import useScrollOutside from 'hooks/use-scroll-outside'
const POINTER_SAFE_INSET = 10
const VIEWPORT_MARGIN = 8
type Position = 'bottom' | 'left' | 'right' | 'top'

const getRectForNode = (node: HTMLElement): Rect => {
  const rect = node.getBoundingClientRect()
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  }
}

const getRectCenter = (rect: Rect): Point => ({
  x: rect.left + rect.width / 2,
  y: rect.top + rect.height / 2,
})

const getViewport = (): Pick<Rect, 'width' | 'height'> => ({
  width: document.body?.clientWidth ?? 0,
  height: window.innerHeight,
})

const getIntersectionWithViewport = (rect: Rect): Rect => {
  const viewport = getViewport()
  const top = Math.max(rect.top, 0)
  const left = Math.max(rect.left, 0)
  const bottom = Math.min(rect.top + rect.height, viewport.height)
  const right = Math.min(rect.left + rect.width, viewport.width)
  return {
    top,
    left,
    width: right - left,
    height: bottom - top,
  }
}

const getPopoverPosition = (
  activatorRect: Rect,
  popoverRect: Rect,
  position: Position,
): Position => {
  const viewport = getViewport()

  if (position === 'bottom') {
    const popoverBottom =
      activatorRect.top +
      activatorRect.height +
      popoverRect.height +
      VIEWPORT_MARGIN
    return popoverBottom > viewport.height ? 'top' : 'bottom'
  }

  if (position === 'right') {
    const popoverRight =
      activatorRect.left +
      activatorRect.width +
      popoverRect.width +
      VIEWPORT_MARGIN
    return popoverRight > viewport.width ? 'left' : 'right'
  }

  return position
}

const getPopoverOffsetX = (
  activatorRect: Rect,
  popoverRect: Rect,
  position: Position,
): number => {
  const getOffsetX = (offset: number): number => {
    const viewport = getViewport()
    const maxOffsetX = viewport.width - popoverRect.width - VIEWPORT_MARGIN
    const offsetX = getPageXOffset() + Math.min(maxOffsetX, offset)
    return Math.round(Math.max(offsetX, VIEWPORT_MARGIN))
  }

  if (position === 'left') {
    return getOffsetX(activatorRect.left - popoverRect.width)
  }

  if (position === 'right') {
    return getOffsetX(activatorRect.left + activatorRect.width)
  }

  return getOffsetX(getRectCenter(activatorRect).x - popoverRect.width / 2)
}

const getPopoverOffsetY = (
  activatorRect: Rect,
  popoverRect: Rect,
  position: Position,
): number => {
  const getOffsetY = (offset: number): number => {
    const viewport = getViewport()
    const maxOffsetY = viewport.height - popoverRect.height - VIEWPORT_MARGIN
    const offsetY = getPageYOffset() + Math.min(maxOffsetY, offset)
    return Math.round(Math.max(offsetY, VIEWPORT_MARGIN))
  }

  if (position === 'top') {
    return getOffsetY(activatorRect.top - popoverRect.height)
  }

  if (position === 'bottom') {
    return getOffsetY(activatorRect.top + activatorRect.height)
  }

  return getOffsetY(getRectCenter(activatorRect).y - popoverRect.height / 2)
}

const getPopoverStyle = (
  activatorRect: Rect,
  popoverRect: Rect,
  position: Position,
): Rect => {
  const viewport = getViewport()
  const width = Math.min(
    popoverRect.width,
    viewport.width - VIEWPORT_MARGIN * 2,
  )
  const height = Math.min(
    popoverRect.height,
    viewport.height - VIEWPORT_MARGIN * 2,
  )
  return {
    top: getPopoverOffsetY(activatorRect, popoverRect, position),
    left: getPopoverOffsetX(activatorRect, popoverRect, position),
    width,
    height,
  }
}

const stopPropagation = (event: React.SyntheticEvent<any>) =>
  event.stopPropagation()

type Props = {
  children: React.ReactNode
  position?: Position
  targetRef: React.RefObject<HTMLElement | null>
  onClose: () => void
  pointer?: 'default' | 'none'
  popoverClassname?: string
}

const PopoverContent = ({
  children,
  position = 'bottom',
  targetRef,
  onClose,
  pointer = 'default',
  popoverClassname,
}: Props): React.ReactElement<'div'> => {
  const popoverRef = useRef<HTMLDivElement | null | undefined>(null)
  const [popoverPosition, setPopoverPosition] = useState<Position>(position)
  const [popoverStyle, setPopoverStyle] = useState<Rect | null | undefined>()
  const [pointerOffset, setPointerOffset] = useState<number>(0)
  const [pointerVisible, setPointerVisible] = useState<boolean>(true)
  useEventListener('keydown', (event: React.KeyboardEvent<HTMLElement>) => {
    if (keyEqualTo(event, 'Escape')) {
      onClose()
    }
  })
  useEventListener('resize', onClose)
  useClickOutside(targetRef, onClose)
  useScrollOutside(targetRef, onClose)
  // BUG: getBoundingClientRect for React ref return incorrect values on the second animation frame when scrollbars are visible
  const activatorRect: Rect | null | undefined = useMemo(
    () => targetRef.current && getRectForNode(targetRef.current),
    [targetRef],
  )
  const setPopoverRef = useCallback(
    (node: HTMLDivElement | null | undefined) => {
      if (popoverRef.current) {
        setPointerOffset(0)
        setPopoverStyle(undefined)
      }

      if (node && activatorRect) {
        const popoverRect: Rect = getRectForNode(node)
        const popoverPosition: Position = getPopoverPosition(
          activatorRect,
          popoverRect,
          position,
        )
        setPopoverPosition(popoverPosition)
        const popoverStyle: Rect = getPopoverStyle(
          activatorRect,
          popoverRect,
          popoverPosition,
        )
        setPopoverStyle(popoverStyle)
        const visibleActivatorRect: Rect =
          getIntersectionWithViewport(activatorRect)
        const pointerOffsetX: number =
          getPageXOffset() +
          getRectCenter(visibleActivatorRect).x -
          popoverStyle.left
        const pointerOffsetY: number =
          getPageYOffset() +
          getRectCenter(visibleActivatorRect).y -
          popoverStyle.top

        if (['bottom', 'top'].includes(popoverPosition)) {
          setPointerOffset(pointerOffsetX)
          setPointerVisible(
            pointerOffsetX <
              popoverStyle.width - VIEWPORT_MARGIN - POINTER_SAFE_INSET,
          )
        }

        if (['left', 'right'].includes(popoverPosition)) {
          setPointerOffset(pointerOffsetY)
          setPointerVisible(
            pointerOffsetY <
              popoverStyle.height - VIEWPORT_MARGIN - POINTER_SAFE_INSET,
          )
        }
      }

      popoverRef.current = node
    },
    [activatorRect],
  )
  return (
    <div
      className={cn(styles.component, popoverClassname, {
        [styles['position--bottom']]: popoverPosition === 'bottom',
        [styles['position--left']]: popoverPosition === 'left',
        [styles['position--right']]: popoverPosition === 'right',
        [styles['position--top']]: popoverPosition === 'top',
      })}
      ref={setPopoverRef}
      // @ts-expect-error[signature-verification-failure]
      style={{ ...popoverStyle, '--pointer-offset': pointerOffset + 'px' }}
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
    >
      {pointer !== 'none' && pointerVisible && (
        <div className={styles.pointer}>
          <div className={styles.shadow} />
        </div>
      )}
      {pointer == 'none' && <div className={styles.offset} />}
      <div className={styles.content}>{children}</div>
    </div>
  )
}

export default PopoverContent
